import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

const inputCls =
  "w-full border border-white/10 bg-[#12151C] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-amber-600/60";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.user?.role !== "admin") {
        setError("This login is for workshop staff only. Customers can sign in at the Customer Portal.");
        return;
      }
      localStorage.setItem("smw_token", data.token);
      localStorage.setItem("smw_last_active", String(Date.now()));
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App flex min-h-screen items-center justify-center px-5" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md border border-white/10 bg-[#12151C] p-8 sm:p-10"
      >
        <div className="font-display text-3xl tracking-[0.12em] text-slate-50">SAGITTARIUS</div>
        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">Workshop Control</div>

        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="staff@email" className={inputCls} data-testid="admin-email-input" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} data-testid="admin-password-input" />
          </div>

          {error && (
            <div className="border border-red-500/50 bg-red-500/10 px-4 py-3 text-xs text-red-300" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="flex w-full items-center justify-center gap-3 bg-amber-600 py-4 font-mono text-sm uppercase tracking-[0.2em] text-black transition-colors duration-200 hover:bg-amber-500 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <Link to="/" className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-amber-500" data-testid="admin-back-home">
          <ArrowLeft size={12} /> Back to site
        </Link>
      </motion.div>
    </div>
  );
}
